import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { UserIndexedDB } from '../interfaces/user-indexed-db';

@Injectable({
    providedIn: 'root'
})
export class IndexedDBService {

    private indexedDB = window.indexedDB;
    public db: IDBDatabase | null = null;
    private encryptionKey = 'rs39D5Fg2jp!eaXhdxQv9J'; // Clave secreta para encriptar y desencriptar datos

    constructor(public router: Router) { }

    private randomCode(length: number) {
        var result = '';
        var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        var charactersLength = characters.length;
        for (var i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() *
                charactersLength));
        }
        return result;
    }

    private cipher(pass: string) {
        return this.randomCode(10) + encodeURIComponent(window.btoa(pass));
    }

    public decipher(pass: string) {
        let password = decodeURIComponent(pass).slice(10, pass.length);
        return window.atob(password) || pass;
    }

    /**
     * Formatea los datos del usuario para almacenarlos en IndexedDB.
     * @param userData Datos del usuario a formatear.
     * @returns Objeto formateado para IndexedDB.
     */
    formatUserData(userData: any): UserIndexedDB {
        return {
            country_id: userData.country_id || "", // Asigna el ID del país o una cadena vacía si no existe
            fuente: userData.fuente || "", // Prioriza 'user_fuente', si no, usa 'fuente'
            id: userData.id || "", // ID único del usuario
            image: userData.user_image || userData.image || "", // Usa 'picture' si está disponible, de lo contrario, 'image'
            last_date: new Date(userData.iat * 1000).toISOString(), // Convierte el timestamp a una fecha ISO
            name: userData.name || "", // Prioriza 'user_name', si no, usa 'name'
            password: this.cipher(userData.password) || "", // No hay contraseña en la data, se deja vacío
            role: userData.role || "", // Rol del usuario
            username: userData.email || userData.username || "", // Usa el email o username según disponibilidad
            darkTheme: false, // Valor por defecto para el tema oscuro
            popoverState: false, // Estado inicial del popover en la UI
            popupTheme: false // Estado inicial del popup de configuración de tema
        };
    }



    /**
     * Abre la base de datos IndexedDB. Si ya está abierta, la reutiliza.
     */
    async openIndexedDB(): Promise<{ status: string }> {
        if (this.db) {
            return { status: 'ok' };
        }

        return new Promise((resolve, reject) => {
            if (!this.indexedDB) {
                reject({ status: 'error', message: 'IndexedDB no es compatible con este navegador' });
                return;
            }

            const request = indexedDB.open('edutindb', 1);

            request.onsuccess = () => {
                this.db = request.result;
                resolve({ status: 'ok' });
            };

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('users')) {
                    db.createObjectStore('users', { keyPath: 'id' });
                }
            };

            request.onerror = () => {
                reject({ status: 'error', message: 'Error al abrir IndexedDB' });
            };
        });
    }

    /**
     * Obtiene datos de un almacén específico en IndexedDB.
     */
    async getData(storeName: string, key: string): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject({ status: 'error', message: 'IndexedDB no está disponible' });
                return;
            }
            const transaction = this.db.transaction([storeName], 'readonly');
            const store = transaction.objectStore(storeName);
            const request = store.get(key);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject({ status: 'error', message: 'Error al obtener datos' });
        });
    }

    /**
     * Obtiene todos los usuarios almacenados en IndexedDB y verifica si están encriptados.
     * Si no lo están, los devuelve sin cambios; si lo están, los desencripta.
     */
    async getAllUsers(): Promise<any[]> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject({ status: 'error', message: 'IndexedDB no está disponible' });
                return;
            }
            const transaction = this.db.transaction(['users'], 'readonly');
            const store = transaction.objectStore('users');
            const request = store.getAll();

            request.onsuccess = () => {
                const users = request.result.map(user => {
                    if (user && typeof user.data === 'string') {
                        try {
                            return this.decryptData(user.data);
                        } catch (error) {
                            console.warn('Error al desencriptar usuario, devolviendo original:', user);
                            return user;
                        }
                    }
                    return user; // Si ya es un objeto, retornarlo sin cambios
                });
                //Ordenar usuarios por el campo last_date
                users.sort((a, b) => {
                    const dateA = new Date(a.last_date).getTime();
                    const dateB = new Date(b.last_date).getTime();
                    return dateB - dateA; // Ordenar de más reciente a más antiguo
                });
                resolve(users);
            };

            request.onerror = () => reject({ status: 'error', message: 'Error al obtener usuarios' });
        });
    }
    /**
     * Guarda o actualiza un usuario en IndexedDB con datos encriptados.
     */
    // async saveOrUpdateUser(user: any): Promise<{ status: string }> {
    //     const existingUser = await this.getUser(user.id);

    //     if (existingUser) {
    //         // user = { ...existingUser, ...user }; // Mantiene los datos existentes y actualiza con los nuevos
    //     }
    //     return this.addUser(user);
    // }

    /**
     * Agrega un usuario a IndexedDB con datos encriptados.
     */
    async addUser(user: any): Promise<{ status: string }> {
        const encryptedUser = this.encryptData(user);
        return this.storeData('users', { id: user.id, data: encryptedUser });
    }

    /**
     * Obtiene un usuario de IndexedDB y desencripta su información.
     */
    async getUser(userId: string): Promise<any> {
        const user = await this.getData('users', userId);
        if (typeof user?.data !== 'string') { // Verifica si aún no está encriptado)
            return user;
        }
        return user ? this.decryptData(user.data) : null;
    }

    /**
     * Encripta un objeto utilizando AES.
     */
    private encryptData(data: any): string {
        return CryptoJS.AES.encrypt(JSON.stringify(data), this.encryptionKey).toString();
    }

    /**
     * Desencripta un objeto encriptado utilizando AES.
     */
    private decryptData(encryptedData: string): any {
        const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
        var userDecrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        userDecrypted.password = this.decipher(userDecrypted.password) || "";
        if (userDecrypted?.username.includes('@phone.edutin.com')) {
            userDecrypted.username = userDecrypted.username.split('@phone.edutin.com')[0];
        }
        return userDecrypted;
    }


    /**
     * Almacena datos en IndexedDB.
     */
    private storeData(storeName: string, data: any): Promise<{ status: string }> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject({ status: 'error', message: 'IndexedDB no está disponible' });
                return;
            }
            const transaction = this.db.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.put(data);

            request.onsuccess = () => resolve({ status: 'ok' });
            request.onerror = () => reject({ status: 'error', message: 'Error al almacenar datos' });
        });
    }

    /**
 * Elimina un usuario de IndexedDB según su ID.
 */
    async deleteUser(userId: string): Promise<{ status: string }> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject({ status: 'error', message: 'IndexedDB no está disponible' });
                return;
            }

            const transaction = this.db.transaction(['users'], 'readwrite');
            const store = transaction.objectStore('users');
            const request = store.delete(userId);

            request.onsuccess = () => resolve({ status: 'ok' });
            request.onerror = () => reject({ status: 'error', message: 'Error al eliminar usuario' });
        });
    }


    /**
     * Migra los usuarios almacenados en IndexedDB sin encriptación y los convierte en datos encriptados.
     */
    async migrateUsers(): Promise<void> {
        if (!this.db) return;
        const transaction = this.db.transaction(['users'], 'readwrite');
        const store = transaction.objectStore('users');
        const request = store.getAll();

        request.onsuccess = () => {
            request.result.forEach(user => {
                if (typeof user.data !== 'string') { // Verifica si aún no está encriptado
                    const encryptedUser = this.encryptData(user);
                    store.put({ id: user.id, data: encryptedUser });
                }
            });
        };
    }




    async migrateUsersFromCoreApp(accounts: any): Promise<void> {
        if (!this.db) return;
        const transaction = this.db.transaction(['users'], 'readwrite');
        const store = transaction.objectStore('users');

        accounts.forEach((user: any) => {
            if (typeof user.data !== 'string') { // Verifica si aún no está encriptado
                const encryptedUser = this.encryptData(user);
                store.put({ id: user.id, data: encryptedUser });
            }
        });

    }

}
